function esc(str) {
  return String(str || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function mkBtn(extraClass, action, label) {
  return `<button class="acb-btn ${extraClass}" data-acb-action="${action}" type="button">${label}</button>`;
}

function buildBannerButtons(c, isStrict) {
  const model = c.consent_model || 'accept_reject_preferences';
  const rejectLabel = esc(c.reject_button_text || 'Reject');
  const acceptLabel = esc(c.accept_button_text || 'Accept');
  const prefsLabel  = esc(c.preferences_button_text || 'Preferences');

  switch (model) {
    case 'accept_reject_preferences':
      return (
        mkBtn('acb-btn-reject', 'reject',      rejectLabel) +
        mkBtn('acb-btn-prefs',  'preferences', prefsLabel) +
        mkBtn('acb-btn-accept', 'accept',      acceptLabel)
      );
    case 'accept_preferences':
      // Strict regions require an equally-prominent Reject on the first surface.
      return (
        (isStrict ? mkBtn('acb-btn-reject', 'reject', rejectLabel) : '') +
        mkBtn('acb-btn-prefs',  'preferences', prefsLabel) +
        mkBtn('acb-btn-accept', 'accept',      acceptLabel)
      );
    case 'do_not_sell':
      return (
        mkBtn('acb-btn-dns',    'do_not_sell', 'Do not sell or share') +
        mkBtn('acb-btn-accept', 'accept',      acceptLabel)
      );
    case 'accept_only':
      // Strict regions: never expose accept-only without a reject path.
      if (isStrict) {
        return (
          mkBtn('acb-btn-reject', 'reject', rejectLabel) +
          mkBtn('acb-btn-accept', 'accept', acceptLabel)
        );
      }
      return mkBtn('acb-btn-accept', 'accept', acceptLabel);
    case 'notice_only':
      // Strict regions: never expose pure dismiss; force a real choice.
      if (isStrict) {
        return (
          mkBtn('acb-btn-reject', 'reject', rejectLabel) +
          mkBtn('acb-btn-accept', 'accept', acceptLabel)
        );
      }
      return mkBtn('acb-btn-dismiss', 'dismiss', 'Dismiss');
    default:
      return mkBtn('acb-btn-accept', 'accept', acceptLabel);
  }
}

function bannerHref(href) {
  const url = String(href || '').trim();
  if (!url) return '#';
  if (/^(https?:|mailto:|tel:)/i.test(url)) return url;
  if (url.startsWith('/') || url.startsWith('#') || url.startsWith('?')) return url;
  return `https://${url}`;
}

function bannerInlineLink(href, label) {
  const url = bannerHref(href);
  const external = url && url !== '#' ? ' target="_blank" rel="noopener noreferrer"' : '';
  return `<a href="${esc(url)}"${external}>${esc(label)}</a>`;
}

function hasPrivacyPolicyUrl(c) {
  return Boolean(String(c?.privacy_policy_url || '').trim());
}

export function buildBannerHTML(c, isStrict) {
  let inlineLinks = '';
  // Match Wix: only render Privacy Policy when a URL is configured.
  if (hasPrivacyPolicyUrl(c)) {
    const privacyLabel = c.privacy_policy_text || 'Privacy Policy';
    inlineLinks += ` ${bannerInlineLink(c.privacy_policy_url, privacyLabel)}`;
  }
  if (c.imprint_link_enabled && (c.imprint_text || 'Imprint')) {
    inlineLinks += (inlineLinks ? ' · ' : ' ') + bannerInlineLink(c.imprint_url, c.imprint_text || 'Imprint');
  }

  // Classic markup — heading + body + equal-width actions (matches admin LivePreview).
  const heading = esc(
    (typeof c.banner_heading === 'string' && c.banner_heading.trim())
      ? c.banner_heading.trim()
      : 'We value your privacy'
  );

  return `
    <div class="acb-inner">
      <div class="acb-text-block">
        <p class="acb-heading">${heading}</p>
        <p class="acb-body">${esc(c.banner_text || '')}${inlineLinks}</p>
      </div>
      <div class="acb-buttons">${buildBannerButtons(c, !!isStrict)}</div>
    </div>
  `;
}

/** Classic prefs — title + chevron collapses/expands the category description. */
function buildClassicCategory(key, title, desc, toggleable, defaultOpen = false) {
  const toggle = toggleable
    ? `<label class="acb-toggle" aria-label="${esc(title)}">
         <input type="checkbox" data-acb-cat="${key}">
         <span class="acb-toggle-track"></span>
         <span class="acb-toggle-thumb"></span>
       </label>`
    : '<span class="acb-always-on">Always on</span>';

  const hasDesc = !!String(desc || '').trim();
  const collapsed = hasDesc && !defaultOpen;
  const titleBlock = hasDesc
    ? `<button type="button" class="acb-cat-details-toggle" data-acb-action="toggle_cat_desc" aria-expanded="${collapsed ? 'false' : 'true'}" aria-label="Toggle ${esc(title)} details">
         <svg class="acb-cat-chevron" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
           <path fill="currentColor" d="M5.4 3.2 10.2 8 5.4 12.8 4.3 11.7 8 8 4.3 4.3z"/>
         </svg>
         <span class="acb-cat-title">${esc(title)}</span>
       </button>`
    : `<span class="acb-cat-title">${esc(title)}</span>`;

  return `
    <div class="acb-category${collapsed ? ' acb-collapsed' : ''}"${hasDesc ? ' data-acb-cat-collapsible' : ''}>
      <div class="acb-cat-header">
        ${titleBlock}
        ${toggle}
      </div>
      ${hasDesc ? `<p class="acb-cat-desc" data-acb-cat-desc>${esc(desc)}</p>` : ''}
    </div>
  `;
}

export function buildPreferencesHTML(c, options = {}) {
  const saveLabel = options.anchored
    ? 'Save'
    : (c.save_choice_button_text || 'Save my choice');
  const classicCategories = `
    ${buildClassicCategory('necessary',   c.necessary_title        || 'Necessary',   c.necessary_description        || '', false, true)}
    ${buildClassicCategory('statistics',  c.statistics_title       || 'Statistics',  c.statistics_description       || '', true, false)}
    ${buildClassicCategory('marketing',   c.marketing_title        || 'Marketing',   c.marketing_description        || '', true, false)}
    ${buildClassicCategory('preferences', c.preferences_cat_title  || 'Preferences', c.preferences_cat_description  || '', true, false)}
  `;

  return `
    <button class="acb-prefs-close" data-acb-action="close_prefs" type="button" aria-label="Close">×</button>
    <p class="acb-prefs-title">${esc(c.pref_popup_title || 'Cookie Preferences')}</p>
    <p class="acb-prefs-desc">${esc(c.pref_popup_description || '')}</p>
    <div class="acb-categories">${classicCategories}</div>
    <div class="acb-prefs-buttons">
      ${mkBtn('acb-btn-reject-all', 'reject_all', esc(c.reject_all_button_text   || 'Reject All'))}
      ${mkBtn('acb-btn-save',       'save_prefs', esc(saveLabel))}
      ${mkBtn('acb-btn-accept-all', 'accept_all', esc(c.accept_all_button_text   || 'Accept All'))}
    </div>
  `;
}

function formatConsentDate(timestamp) {
  if (!timestamp) return '';
  try {
    const d = new Date(timestamp);
    if (Number.isNaN(d.getTime())) return '';
    const datePart = d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    return datePart.replace(/\u202f/g, ' ');
  } catch (_) {
    return '';
  }
}

function statusIcon(on) {
  if (on) {
    return (
      '<span class="acb-settings-status-icon acb-settings-status-on" aria-hidden="true">' +
      '<svg viewBox="0 0 16 16" width="14" height="14" focusable="false">' +
      '<path fill="currentColor" d="M6.5 11.2 3.3 8l1.1-1.1 2.1 2.1 4.9-4.9L12.5 5.2z"/>' +
      '</svg></span>'
    );
  }
  return (
    '<span class="acb-settings-status-icon acb-settings-status-off" aria-hidden="true">' +
    '<svg viewBox="0 0 16 16" width="14" height="14" focusable="false">' +
    '<path fill="currentColor" d="M4.2 3.1 3.1 4.2 6.9 8l-3.8 3.8 1.1 1.1L8 9.1l3.8 3.8 1.1-1.1L9.1 8l3.8-3.8-1.1-1.1L8 6.9z"/>' +
    '</svg></span>'
  );
}

function buildStatusRow(label, on) {
  const state = on ? 'granted' : 'denied';
  return (
    `<li class="acb-settings-status-item" data-acb-status="${state}">` +
    `${statusIcon(on)}<span>${esc(label)}</span></li>`
  );
}

/** Icon-only status panel — visual match for post-consent “Your cookie settings”. */
export function buildCookieSettingsHTML(c, consent) {
  const cats = (consent && consent.categories) || {};
  const statsOn = !!cats.statistics;
  const marketingOn = !!cats.marketing;
  const prefsOn = !!cats.preferences;
  const dateStr = formatConsentDate(consent && consent.timestamp);
  const statsLabel = (c.statistics_title || 'Statistics') + ' cookies';
  const marketingLabel = (c.marketing_title || 'Marketing') + ' cookies';
  const prefsLabel = (c.preferences_cat_title || 'Preferences') + ' cookies';

  return `
    <div class="acb-settings-header">
      <p class="acb-settings-title">${esc(c.cookie_settings_title || 'Your cookie settings')}</p>
      <button class="acb-settings-close" data-acb-action="close_settings" type="button" aria-label="Close">
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M4.2 3.1 3.1 4.2 6.9 8l-3.8 3.8 1.1 1.1L8 9.1l3.8 3.8 1.1-1.1L9.1 8l3.8-3.8-1.1-1.1L8 6.9z"/>
        </svg>
      </button>
    </div>
    <div class="acb-settings-body">
      <p class="acb-settings-state-label">Current state:</p>
      <ul class="acb-settings-status-list">
        ${buildStatusRow(statsLabel, statsOn)}
        ${buildStatusRow(marketingLabel, marketingOn)}
        ${buildStatusRow(prefsLabel, prefsOn)}
      </ul>
      <button class="acb-settings-details-toggle" data-acb-action="toggle_details" type="button" aria-expanded="true">
        <span class="acb-settings-details-label">Hide details</span>
        <svg class="acb-settings-chevron" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
          <path fill="currentColor" d="M3.2 10.2 8 5.4l4.8 4.8-1.1 1.1L8 7.6l-3.7 3.7z"/>
        </svg>
      </button>
      <div class="acb-settings-details" data-acb-details>
        <p class="acb-settings-details-row">
          <strong>Last consent date:</strong>
          <span>${esc(dateStr || '—')}</span>
        </p>
      </div>
    </div>
    <div class="acb-settings-actions">
      ${mkBtn('acb-btn-withdraw', 'withdraw_consent', esc(c.withdraw_consent_text || 'Withdraw consent'))}
      ${mkBtn('acb-btn-change', 'change_consent', esc(c.change_consent_text || 'Change consent'))}
    </div>
  `;
}
